/**
 * /app/history — closed-trade aggregations.
 *
 * Bucket switcher (day / week / month) + period range. Above the
 * table: a bar-chart of total PnL per bucket (green/rose by sign).
 * Click a row → drawer with the individual trades inside that bucket.
 */
import { useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import {
  Bar, BarChart, CartesianGrid, ReferenceLine, ResponsiveContainer,
  Tooltip, XAxis, YAxis, Cell,
} from 'recharts'

import { Drawer } from '@/components/ui/Drawer'
import { Select } from '@/components/ui/Select'
import {
  type HistoryBucket,
  type HistoryBucketKind,
  type HistoryTrade,
  useHistorySummary,
  useHistoryTrades,
} from '@/hooks/useDashboardApi'
import { cn } from '@/lib/cn'
import { formatNumber, formatPct, formatUsd, timeAgo } from '@/lib/format'

const BUCKET_OPTS: HistoryBucketKind[] = ['day', 'week', 'month']
const DAYS_OPTS = [30, 90, 180, 365]

export default function History() {
  const { t } = useTranslation('app')

  const [bucket, setBucket] = useState<HistoryBucketKind>('day')
  const [days, setDays] = useState(90)
  const [drillBucket, setDrillBucket] = useState<HistoryBucket | null>(null)

  const fromIso = useMemo(
    () => new Date(Date.now() - days * 86400_000).toISOString(),
    [days],
  )

  const { data, isLoading } = useHistorySummary(bucket, fromIso)

  // Recharts wants chronological ASC for natural bar order
  const chartData = useMemo(
    () => (data?.items ?? []).slice().reverse().map((b) => ({
      label: bucketLabel(b.bucket_start, bucket),
      pnl: b.total_pnl,
      trades: b.trades,
    })),
    [data, bucket],
  )

  return (
    <div className="p-4 sm:p-6 max-w-[1200px]">
      <header className="mb-4">
        <h1 className="text-2xl font-semibold tracking-tight">
          {t('history.title')}
        </h1>
        <p className="text-sm text-textDim mt-1">{t('history.subtitle')}</p>
      </header>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-2 mb-4">
        <Select
          value={bucket}
          onChange={(e) => setBucket(e.target.value as HistoryBucketKind)}
        >
          {BUCKET_OPTS.map((b) => (
            <option key={b} value={b}>{t(`history.bucket.${b}`)}</option>
          ))}
        </Select>
        <Select
          value={String(days)}
          onChange={(e) => setDays(Number(e.target.value))}
        >
          {DAYS_OPTS.map((d) => (
            <option key={d} value={d}>
              {t('positions.filter.days', { count: d })}
            </option>
          ))}
        </Select>
      </div>

      {/* Bar chart */}
      {chartData.length > 0 && (
        <section className="bg-ink border border-line p-4 mb-4">
          <div className="text-xs text-textFaint uppercase tracking-wider mb-2">
            {t('history.chart.title', { bucket: t(`history.bucket.${bucket}`) })}
          </div>
          <div className="h-[200px]">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={chartData} margin={{ top: 6, right: 6, left: 0, bottom: 0 }}>
                <CartesianGrid stroke="var(--line)" strokeDasharray="2 4" vertical={false} />
                <XAxis
                  dataKey="label"
                  tick={{ fill: 'var(--text-faint)', fontSize: 10, fontFamily: 'JetBrains Mono' }}
                  axisLine={{ stroke: 'var(--line)' }}
                  tickLine={false}
                  minTickGap={32}
                />
                <YAxis
                  tick={{ fill: 'var(--text-faint)', fontSize: 10, fontFamily: 'JetBrains Mono' }}
                  axisLine={{ stroke: 'var(--line)' }}
                  tickLine={false}
                  tickFormatter={(v: number) => formatUsd(v, { compact: true })}
                  width={56}
                />
                <Tooltip
                  contentStyle={{
                    background: 'var(--elevated)',
                    border: '1px solid var(--line)',
                    borderRadius: 4,
                    fontSize: 12,
                  }}
                  labelStyle={{ color: 'var(--text-faint)' }}
                  formatter={(v) =>
                    typeof v === 'number' ? formatUsd(v, { sign: true }) : String(v ?? '—')
                  }
                />
                <ReferenceLine y={0} stroke="var(--text-faint)" />
                <Bar dataKey="pnl" name={t('history.chart.pnl_legend')}>
                  {chartData.map((d, i) => (
                    <Cell key={i} fill={d.pnl >= 0 ? 'var(--pulse)' : 'var(--rose)'} />
                  ))}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </div>
        </section>
      )}

      {/* Table */}
      <section className="bg-ink border border-line">
        {isLoading || !data ? (
          <div className="px-5 py-10 text-center text-textFaint text-sm">—</div>
        ) : data.items.length === 0 ? (
          <div className="px-5 py-10 text-center text-textFaint text-sm">
            {t('history.empty')}
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="text-textDim text-xs uppercase tracking-wider border-b border-line bg-elevated/40">
                <tr>
                  <Th className="text-left px-5">{t('history.table.period')}</Th>
                  <Th className="text-right">{t('history.table.trades')}</Th>
                  <Th className="text-right">{t('history.table.win_rate')}</Th>
                  <Th className="text-right">{t('history.table.pnl')}</Th>
                  <Th className="text-right">{t('history.table.avg')}</Th>
                  <Th className="text-right">{t('history.table.best')}</Th>
                  <Th className="text-right px-5">{t('history.table.worst')}</Th>
                </tr>
              </thead>
              <tbody className="font-mono">
                {data.items.map((b) => (
                  <BucketRow
                    key={b.bucket_start}
                    b={b}
                    bucket={bucket}
                    onClick={() => setDrillBucket(b)}
                  />
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>

      <DrillDrawer
        bucket={drillBucket}
        bucketKind={bucket}
        onClose={() => setDrillBucket(null)}
      />
    </div>
  )
}

/* ─── Bucket row ─────────────────────────────────────────────────── */

function BucketRow({
  b, bucket, onClick,
}: {
  b: HistoryBucket
  bucket: HistoryBucketKind
  onClick: () => void
}) {
  const pnlTone = b.total_pnl > 0 ? 'text-pulse'
                : b.total_pnl < 0 ? 'text-rose' : 'text-textDim'
  return (
    <tr
      onClick={onClick}
      className="border-b border-line/40 hover:bg-elevated/30 cursor-pointer transition-colors"
    >
      <td className="px-5 py-2 font-semibold">{bucketLabel(b.bucket_start, bucket)}</td>
      <td className="text-right py-2">
        <span className="text-text">{b.trades}</span>
        <span className="ml-2 text-textFaint text-xs">
          {b.wins}W · {b.losses}L
        </span>
      </td>
      <td className="text-right py-2">
        {b.win_rate_pct != null ? formatPct(b.win_rate_pct, { digits: 0 }) : '—'}
      </td>
      <td className={cn('text-right py-2 font-semibold tabular-nums', pnlTone)}>
        {formatUsd(b.total_pnl, { sign: true })}
      </td>
      <td className="text-right py-2 text-textDim tabular-nums">
        {b.avg_trade_pnl != null ? formatUsd(b.avg_trade_pnl, { sign: true }) : '—'}
      </td>
      <td className="text-right py-2 text-pulse tabular-nums">
        {b.best_trade_pnl != null ? formatUsd(b.best_trade_pnl, { sign: true }) : '—'}
      </td>
      <td className="text-right px-5 py-2 text-rose tabular-nums">
        {b.worst_trade_pnl != null ? formatUsd(b.worst_trade_pnl, { sign: true }) : '—'}
      </td>
    </tr>
  )
}

/* ─── Drill drawer (trades inside a bucket) ──────────────────────── */

function DrillDrawer({
  bucket, bucketKind, onClose,
}: {
  bucket: HistoryBucket | null
  bucketKind: HistoryBucketKind
  onClose: () => void
}) {
  const { t } = useTranslation('app')

  // Compute window for the bucket's range.
  const { fromIso, toIso } = useMemo(() => {
    if (!bucket) return { fromIso: null, toIso: null }
    const start = new Date(bucket.bucket_start)
    const end = new Date(start)
    if (bucketKind === 'day')   end.setUTCDate(end.getUTCDate() + 1)
    if (bucketKind === 'week')  end.setUTCDate(end.getUTCDate() + 7)
    if (bucketKind === 'month') end.setUTCMonth(end.getUTCMonth() + 1)
    return { fromIso: start.toISOString(), toIso: end.toISOString() }
  }, [bucket, bucketKind])

  const { data, isLoading } = useHistoryTrades(fromIso, toIso)

  return (
    <Drawer
      open={!!bucket}
      onClose={onClose}
      title={bucket ? bucketLabel(bucket.bucket_start, bucketKind) : '—'}
      subtitle={bucket ? `${bucket.trades} ${t('history.trades_count')} · ${formatUsd(bucket.total_pnl, { sign: true })}` : undefined}
      widthClassName="w-[640px]"
    >
      {isLoading || !data ? (
        <div className="px-5 py-10 text-center text-textFaint text-sm">—</div>
      ) : data.items.length === 0 ? (
        <div className="px-5 py-10 text-center text-textFaint text-sm">
          {t('history.empty_drill')}
        </div>
      ) : (
        <ul className="divide-y divide-line/40 font-mono">
          {data.items.map((tr) => <TradeRow key={tr.id} tr={tr} />)}
        </ul>
      )}
    </Drawer>
  )
}

function TradeRow({ tr }: { tr: HistoryTrade }) {
  const { t } = useTranslation('app')
  const pnlTone = tr.realized_pnl > 0 ? 'text-pulse'
                : tr.realized_pnl < 0 ? 'text-rose' : 'text-textDim'
  return (
    <li className="px-5 py-2 flex items-center gap-3 text-xs">
      <span className={cn(
        'pill',
        tr.direction === 'long' ? 'bg-pulse-faint text-pulse' : 'bg-rose-faint text-rose',
      )}>
        {tr.direction === 'long' ? 'L' : 'S'}
      </span>
      <span className="font-semibold w-24 shrink-0">{tr.symbol}</span>
      <span className={cn('tabular-nums w-24 text-right', pnlTone)}>
        {formatUsd(tr.realized_pnl, { sign: true })}
      </span>
      <span className="tabular-nums text-textFaint w-16 text-right">
        {tr.realized_pnl_pct != null ? formatPct(tr.realized_pnl_pct, { sign: true }) : '—'}
      </span>
      {tr.exit_reason && (
        <span className="pill bg-elevated text-textDim">
          {t(`exit_reason.${tr.exit_reason}`, { defaultValue: tr.exit_reason })}
        </span>
      )}
      <span className="ml-auto text-textFaint">{timeAgo(tr.closed_at)}</span>
    </li>
  )
}

/* ─── building blocks ───────────────────────────────────────────── */

function Th({
  className, children,
}: {
  className?: string
  children: React.ReactNode
}) {
  return <th className={cn('py-2 font-medium', className)}>{children}</th>
}

function bucketLabel(iso: string, bucket: HistoryBucketKind): string {
  const d = new Date(iso)
  if (bucket === 'month') {
    return d.toLocaleDateString([], { month: 'short', year: 'numeric' })
  }
  if (bucket === 'week') {
    // Show range start
    return d.toLocaleDateString([], { day: '2-digit', month: 'short' })
  }
  return d.toLocaleDateString([], { day: '2-digit', month: 'short' })
}

// Suppress unused
void formatNumber